import type {
  StanjeStrnitve,
  Strnitev,
  IndeksBaze,
  IzvirneVrstice,
  PokrivalniIndeks,
  StanjeBaze as StanjeBazeTip,
  VarovanjeIndeksa,
} from '@koda/shared';
import { useEffect, useState, type JSX } from 'react';

import { api } from '../lib/api';
import { datumInUra } from '../lib/format';

/** 1,2 GB — velikosti v enoti, ki jo je mogoče brati brez štetja ničel. */
function velikost(bajtov: number): string {
  const enote = ['B', 'kB', 'MB', 'GB', 'TB'];
  let n = bajtov;
  let i = 0;
  while (n >= 1024 && i < enote.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toLocaleString('sl-SI', {
    minimumFractionDigits: i === 0 ? 0 : 1,
    maximumFractionDigits: i === 0 ? 0 : 1,
  })} ${enote[i]!}`;
}

/** 1.234 — slovenski zapis loči tisočice tudi pri štirimestnih številih (CLAUDE.md, pravilo 10). */
const stevilo = (n: number): string => n.toLocaleString('sl-SI', { useGrouping: 'always' });

/**
 * Meritev, ki je ni.
 *
 * Nič in »ni podatka« nista isto: nič bi pomenilo, da dnevnika ni oziroma da
 * je tabela strnjena. Nekaterih številk vloga aplikacije ne sme prebrati in
 * takrat je pošteno povedati prav to.
 */
const NI_PODATKA = 'ni podatka';

/** Velikost ali »ni podatka«. */
const velikostAli = (bajtov: number | null): string =>
  bajtov === null ? NI_PODATKA : velikost(bajtov);

/**
 * Prazen prostor kot delež tabele.
 *
 * Absolutna številka sama pove malo: 50 MB praznega je pri tabeli z 80 MB
 * podatkov veliko, pri tabeli s 5 GB pa nič. Delež pove, ali se čiščenje z
 * izključno ključavnico sploh splača.
 */
function delezProstega(prosto: number | null, podatki: number): string {
  if (prosto === null) return NI_PODATKA;
  if (podatki <= 0) return velikost(0);
  const delez = Math.round((prosto / podatki) * 100);
  return `${velikost(prosto)} (${String(delez)} %)`;
}

/** Delež v odstotkih, zaokrožen na celo število. */
const odstotek = (del: number, celota: number): string =>
  celota <= 0 ? '0 %' : `${String(Math.round((del / celota) * 100))} %`;

/**
 * Izvirne vrstice pri knjižbah in tri možnosti njihove hrambe
 * (docs/ARHITEKTURA.md, 5.6, »trojna primerjava«).
 *
 * Številke so podlaga za odločitev, ne sprememba: dokler se ne odločimo,
 * se pri knjižbah ne spremeni nič. Možnost 3 je mogoča samo pri uvozih z
 * arhiviranim izvirnikom; pri ostalih izvirna vrstica ostane, kot je, zato
 * je v vrstici »Skupaj« pri možnosti 3 zanje šteta sedanja velikost.
 */
function KarticaIzvirnihVrstic({ m, baza }: { m: IzvirneVrstice; baza: number }): JSX.Element {
  const zdaj = m.zArhivom.zdaj + m.brezArhiva.zdaj;
  const moznost2 = m.zArhivom.nepreslikani + m.brezArhiva.nepreslikani;
  const moznost3 = (m.zArhivom.lokacija ?? 0) + m.brezArhiva.zdaj;
  const zPrihrankom = (bajtov: number): string =>
    `${velikost(bajtov)} (−${velikost(Math.max(0, zdaj - bajtov))})`;
  return (
    <section className="kartica">
      <h2>Izvirne vrstice pri knjižbah</h2>
      <p>
        Izvirne vrstice zasedajo <strong>{velikost(zdaj)}</strong>, to je{' '}
        <strong>{odstotek(zdaj, baza)}</strong> baze. Po možnosti 2 bi zasedale{' '}
        <strong>{zPrihrankom(moznost2)}</strong>, po možnosti 3{' '}
        <strong>{zPrihrankom(moznost3)}</strong>.
      </p>
      <p className="drobno">
        Možnost 2: pri knjižbi ostanejo samo nepreslikani stolpci. Možnost 3: pri knjižbi ostane
        samo lokacija vrstice v arhiviranem izvirniku.
      </p>
      {m.zLokacijo > 0 && (
        <p>
          Knjižb, ki že hranijo samo lokacijo vrstice (možnost 3 pri novih uvozih):{' '}
          <strong>{stevilo(m.zLokacijo)}</strong>.
        </p>
      )}
      <p>
        Novi uvozi glavne knjige zdaj zapisujejo{' '}
        {m.lokacijeVklopljene ? (
          <>
            <strong>samo lokacijo vrstice v arhiviranem izvirniku</strong> (stikalo{' '}
            <code>IZVIRNE_VRSTICE_V_ARHIVU</code> je v tekoči storitvi vklopljeno).
          </>
        ) : (
          <>
            <strong>vrednosti pri knjižbi</strong> (stikalo <code>IZVIRNE_VRSTICE_V_ARHIVU</code> v tekoči
            storitvi ni vklopljeno; velja šele po ponovnem zagonu z vrednostjo <code>true</code>).
          </>
        )}
      </p>
      <table className="knjizbe izvirne-vrstice">
        <thead>
          <tr>
            <th>Uvozi</th>
            <th className="stevilka">Uvozov</th>
            <th className="stevilka">Knjižb</th>
            <th className="stevilka">Zdaj</th>
            <th className="stevilka" title="Samo nepreslikani stolpci">Možnost 2</th>
            <th className="stevilka" title="Samo lokacija vrstice">Možnost 3</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>z arhiviranim izvirnikom</td>
            <td className="stevilka">{stevilo(m.zArhivom.uvozov)}</td>
            <td className="stevilka">{stevilo(m.zArhivom.knjizb)}</td>
            <td className="stevilka">{velikost(m.zArhivom.zdaj)}</td>
            <td className="stevilka">{velikost(m.zArhivom.nepreslikani)}</td>
            <td className="stevilka">{velikost(m.zArhivom.lokacija ?? 0)}</td>
          </tr>
          <tr>
            <td>brez izvirnika</td>
            <td className="stevilka">{stevilo(m.brezArhiva.uvozov)}</td>
            <td className="stevilka">{stevilo(m.brezArhiva.knjizb)}</td>
            <td className="stevilka">{velikost(m.brezArhiva.zdaj)}</td>
            <td className="stevilka">{velikost(m.brezArhiva.nepreslikani)}</td>
            <td className="stevilka">ostane {velikost(m.brezArhiva.zdaj)}</td>
          </tr>
          <tr className="vsota">
            <td>Skupaj</td>
            <td className="stevilka">{stevilo(m.zArhivom.uvozov + m.brezArhiva.uvozov)}</td>
            <td className="stevilka">{stevilo(m.zArhivom.knjizb + m.brezArhiva.knjizb)}</td>
            <td className="stevilka">{velikost(zdaj)}</td>
            <td className="stevilka">{velikost(moznost2)}</td>
            <td className="stevilka">{velikost(moznost3)}</td>
          </tr>
        </tbody>
      </table>
      <p className="namig">
        {m.natancno
          ? `Izmerjeno čez vse knjižbe (${stevilo(m.vzorec)}).`
          : `Ocena iz naključnega vzorca ${stevilo(m.vzorec)} knjižb, preračunana na vse knjižbe skupine.`}{' '}
        Velikosti meri baza sama. <em>Nepreslikani stolpci</em> so tisti, ki jih uvoz ne prenese
        v knjižbo ali dimenzije (po današnji preslikavi podjetja). <em>Lokacija vrstice</em> je
        številka vrstice v arhiviranem izvirniku, {stevilo(m.bajtovLokacije)} B na knjižbo; mogoča
        je samo pri uvozih z izvirnikom, pri ostalih izvirna vrstica ostane. Uvozi brez izvirnika
        so tisti izpred arhiva in tisti, katerih izvirnik je bil po pravilih hrambe izbrisan.
        Meritev ničesar ne spremeni (docs/ARHITEKTURA.md, 5.6).
      </p>
    </section>
  );
}

/** dd. mm. yyyy iz zapisa ISO; prazno, kadar datuma ni. */
function kdaj(iso: string | null): string {
  if (iso === null) return 'nikoli';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('sl-SI', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Nazivi poročil za pregled izrisov (skrbniška stran je slovenska). */
const POROCILA: Record<string, string> = {
  hitri: 'Hitri pregled',
  rezultat: 'Rezultat (IPI)',
  'bs-s': 'Bilanca stanja',
  cf: 'Denarni tok',
  primerjava: 'Primerjava z letom prej',
  bs: 'Bilanca stanja s spremembami',
  kazalniki: 'Kazalniki',
  obcutljivost: 'Občutljivost',
};

/** 850 ms oziroma 1,25 s. */
function trajanje(ms: number): string {
  if (ms < 1000) return `${stevilo(ms)} ms`;
  return `${(ms / 1000).toLocaleString('sl-SI', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} s`;
}

/**
 * Stanje pokrivalnega indeksa z besedami. Gradi se v ozadju ob zagonu, zato
 * mora biti tiha odpoved vidna na pogled (docs/ARHITEKTURA.md, 5.7).
 */
function stanjeIndeksa(i: PokrivalniIndeks): { besedilo: string; vRedu: boolean } {
  if (!i.obstaja) {
    return { besedilo: 'Indeks ne obstaja (ni zgrajen). Poročila delujejo, a počasneje; naslednji zagon ga poskusi zgraditi.', vRedu: false };
  }
  if (!i.veljaven) {
    return {
      besedilo:
        'Indeks je neveljaven: gradnja še teče (takoj po namestitvi) ali pa je spodletela. Baza ga ne uporablja; naslednji zagon ga odstrani in zgradi znova.',
      vRedu: false,
    };
  }
  if (!i.ustreza) {
    return { besedilo: 'Indeks ima stolpce prejšnje različice; naslednji zagon ga zamenja.', vRedu: false };
  }
  return { besedilo: 'Indeks obstaja in je veljaven.', vRedu: true };
}

/**
 * Kdaj je bil obstoječi pokrivalni indeks zgrajen. »Čas ni zapisan« velja
 * samo za dokončan indeks; nedokončanega ne sme prikazati kot starega.
 * Manjkajočega indeksa ta vrstica ne opisuje - to pove opozorilo nad njo.
 */
function gradnjaIndeksa(i: PokrivalniIndeks): string {
  if (i.zgrajen !== null) {
    return `${datumInUra(i.zgrajen)}${i.trajanjeMs === null ? '' : `, gradnja ${trajanje(i.trajanjeMs)}`}`;
  }
  if (!i.veljaven) return 'ne — gradnja ni dokončana';
  return 'čas ni zapisan (zgrajen pred to različico portala)';
}

const VAROVANJE: Record<VarovanjeIndeksa, string> = {
  primarni: 'primarni ključ',
  enolicni: 'enoličnost',
  izkljucitev: 'omejitev',
};

/** Indeksi, združeni po tabeli, v vrstnem redu, kot jih vrne strežnik. */
function poTabelah(indeksi: IndeksBaze[]): [string, IndeksBaze[]][] {
  const skupine = new Map<string, IndeksBaze[]>();
  for (const i of indeksi) skupine.set(i.tabela, [...(skupine.get(i.tabela) ?? []), i]);
  return [...skupine];
}

/**
 * Opomba ob indeksu knjižb. Indeks, ki varuje pravilo, je potreben tudi brez
 * branj; pri navadnem je nič branj le razlog za pregled, ne dokaz, da ni
 * potreben (statistika je lahko prekratka).
 */
function opombaIndeksa(i: IndeksBaze): { besedilo: string; zaPregled: boolean } {
  if (i.varuje !== null) return { besedilo: VAROVANJE[i.varuje], zaPregled: false };
  if (i.branj === 0) return { besedilo: 'za pregled', zaPregled: true };
  return { besedilo: '', zaPregled: false };
}

/**
 * Stanje baze (samo skrbnik).
 *
 * Prostora na strežniku ni mogoče zmanjšati: ko je disk poln, je edini izhod
 * večji paket. Zato ga je treba spremljati, preden se spet napolni — koliko
 * zaseda vsaka tabela, koliko indeksi in kako hitro raste število knjižb
 * (docs/ARHITEKTURA.md, 5.6).
 */
/** Minute v obliki za vnos `datetime-local` (krajevni čas, brez sekund). */
function zaVnos(d: Date): string {
  const dv = (n: number): string => String(n).padStart(2, '0');
  return `${String(d.getFullYear())}-${dv(d.getMonth() + 1)}-${dv(d.getDate())}T${dv(d.getHours())}:${dv(d.getMinutes())}`;
}

const STANJA: Record<Strnitev['stanje'], string> = {
  scheduled: 'načrtovana',
  running: 'v teku',
  done: 'opravljena',
  failed: 'ni uspela',
  cancelled: 'preklicana',
};

function OpisStrnitve({ s }: { s: Strnitev }): JSX.Element {
  return (
    <div className="strnitev-opis">
      <p>
        <strong>{STANJA[s.stanje]}</strong>: {s.tabele.join(', ')}, ob {datumInUra(s.ob)}
        {s.zahteval !== null && <> (zahteval {s.zahteval})</>}
        {s.konec !== null && s.stanje !== 'cancelled' && <>, končana {datumInUra(s.konec)}</>}.
      </p>
      {s.odlog !== null && s.stanje === 'scheduled' && (
        <p className="opozorilo">
          Ob uri izvedbe je bila ovira, zato je strnitev odložena in se poskusi znova ob navedeni uri: {s.odlog}
        </p>
      )}
      {s.izidi.length > 0 && (
        <ul>
          {s.izidi.map((i) => (
            <li key={i.tabela}>
              <code>{i.tabela}</code>:{' '}
              {i.faza === 'koncano' && i.prej !== null && i.potem !== null ? (
                <>
                  {velikost(i.prej)} → {velikost(i.potem)} (sproščenih {velikost(Math.max(0, i.prej - i.potem))}
                  {i.trajanjeMs !== null && <>, {trajanje(i.trajanjeMs)}</>})
                </>
              ) : (
                <span className="opozorilo">
                  {i.faza === 'strnitev'
                    ? 'strnitev ni uspela, tabela je nespremenjena'
                    : i.faza === 'ciscenje'
                      ? 'strnjena, čiščenje po njej pa ne – poženi VACUUM (ANALYZE) ročno'
                      : 'strnjena, meritve ni bilo mogoče prebrati'}
                  {i.napaka !== null && <>: {i.napaka}</>}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Strnitev baze (VACUUM FULL) iz portala (docs/ARHITEKTURA.md, 5.6): razlaga,
 * stanje, nacrtovanje zdaj ali ob uri z vnosom gesla, preklic. Dokler je
 * strnitev nacrtovana ali v teku, se stanje poizveduje.
 */
function KarticaStrnitve(): JSX.Element {
  const [stanje, setStanje] = useState<StanjeStrnitve | null>(null);
  const [napaka, setNapaka] = useState<string | null>(null);
  const [tabele, setTabele] = useState<string[]>([]);
  const [kdajIzvesti, setKdajIzvesti] = useState<'zdaj' | 'ob'>('zdaj');
  const [ob, setOb] = useState(() => zaVnos(new Date(Date.now() + 3600_000)));
  const [geslo, setGeslo] = useState('');
  const [posiljam, setPosiljam] = useState(false);

  const osvezi = (): void => {
    api
      .strnitev()
      .then((s) => {
        setStanje(s);
        setTabele((t) => (t.length === 0 ? s.tabele.map((x) => x.ime) : t));
      })
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : 'Stanja strnitve ni bilo mogoče prebrati.');
      });
  };
  useEffect(osvezi, []);
  const aktivna = stanje?.aktivna ?? null;
  useEffect(() => {
    if (aktivna === null) return;
    const t = window.setInterval(osvezi, 5000);
    return () => {
      window.clearInterval(t);
    };
  }, [aktivna]);

  const nacrtuj = (): void => {
    setPosiljam(true);
    setNapaka(null);
    api
      .nacrtujStrnitev({ tabele, ob: kdajIzvesti === 'zdaj' ? null : new Date(ob).toISOString(), geslo })
      .then(() => {
        setGeslo('');
        osvezi();
      })
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : 'Strnitve ni bilo mogoče načrtovati.');
      })
      .finally(() => {
        setPosiljam(false);
      });
  };

  const preklici = (id: string): void => {
    api
      .prekliciStrnitev(id)
      .then(osvezi)
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : 'Strnitve ni bilo mogoče preklicati.');
      });
  };

  return (
    <section className="kartica" aria-labelledby="strnitev-baze">
      <h2 id="strnitev-baze">Strnitev baze (VACUUM FULL)</h2>
      <p className="namig">
        <strong>Kaj naredi:</strong> ukaz <code>VACUUM FULL</code> tabelo prepiše na novo brez
        praznega prostora in prostor vrne disku. Prazen prostor nastane, ko se vrstice prepišejo
        (ponovno razvrščanje, nadomestni uvoz); navadno čiščenje ga ponovno uporabi, disku ga
        vrne samo strnitev. Takoj po njej portal sam požene še navadno čiščenje, da so poročila
        spet hitra.
      </p>
      <p className="namig">
        <strong>Kaj to pomeni:</strong> med strnitvijo je tabela zaklenjena, zato je{' '}
        <strong>portal za vse uporabnike nedosegljiv</strong> (vzdrževalni način, tudi poročila);
        po izkušnji traja od nekaj sekund do nekaj minut na tabelo. Smiselna je, kadar je
        tabela na disku bistveno večja od svojih podatkov (po večjem ponovnem razvrščanju ali
        več nadomestnih uvozih). Prekinjena strnitev ni nevarna: tabela ostane nespremenjena.
      </p>
      {napaka !== null && <p className="napaka">{napaka}</p>}
      {stanje === null && napaka === null && <p>Berem …</p>}
      {stanje !== null && (
        <>
          {stanje.aktivna !== null && (
            <div className={stanje.aktivna.stanje === 'running' ? 'opozorilo' : 'obvestilo'}>
              <OpisStrnitve s={stanje.aktivna} />
              {stanje.aktivna.stanje === 'running' && <p>Strnitev teče; portal je v vzdrževalnem načinu.</p>}
              {stanje.aktivna.stanje === 'scheduled' && (
                <button
                  type="button"
                  className="gumb"
                  onClick={() => {
                    preklici(stanje.aktivna!.id);
                  }}
                >
                  Prekliči načrtovano strnitev
                </button>
              )}
            </div>
          )}
          {stanje.zadnja !== null && (
            <>
              <h3>Zadnja strnitev</h3>
              <OpisStrnitve s={stanje.zadnja} />
            </>
          )}
          {stanje.aktivna === null && (
            <form
              className="obrazec"
              onSubmit={(d) => {
                d.preventDefault();
                nacrtuj();
              }}
            >
              <h3>Načrtuj strnitev</h3>
              <fieldset>
                <legend>Tabele</legend>
                {stanje.tabele.map((t) => (
                  <label key={t.ime}>
                    <input
                      type="checkbox"
                      checked={tabele.includes(t.ime)}
                      onChange={(d) => {
                        setTabele((prej) => (d.target.checked ? [...prej, t.ime] : prej.filter((x) => x !== t.ime)));
                      }}
                    />{' '}
                    <code>{t.ime}</code> <span className="drobno">({velikost(t.velikost)})</span>
                  </label>
                ))}
              </fieldset>
              <fieldset>
                <legend>Kdaj</legend>
                <label>
                  <input type="radio" name="kdaj" checked={kdajIzvesti === 'zdaj'} onChange={() => { setKdajIzvesti('zdaj'); }} /> zdaj
                </label>
                <label>
                  <input type="radio" name="kdaj" checked={kdajIzvesti === 'ob'} onChange={() => { setKdajIzvesti('ob'); }} /> ob uri{' '}
                  <input
                    type="datetime-local"
                    aria-label="Čas izvedbe"
                    value={ob}
                    disabled={kdajIzvesti !== 'ob'}
                    onChange={(d) => {
                      setOb(d.target.value);
                    }}
                  />
                </label>
                <p className="drobno">Načrtovano strnitev izvede strežnik ob uri tudi, če ta stran ni odprta.</p>
              </fieldset>
              <label>
                Tvoje geslo (potrditev posega)
                <input
                  type="password"
                  autoComplete="current-password"
                  value={geslo}
                  onChange={(d) => {
                    setGeslo(d.target.value);
                  }}
                />
              </label>
              {stanje.ovire.length > 0 && (
                <ul className="opozorilo">
                  {stanje.ovire.map((o) => (
                    <li key={o}>{o}</li>
                  ))}
                </ul>
              )}
              <div className="gumbi">
                <button type="submit" className="gumb" disabled={posiljam || geslo === '' || tabele.length === 0 || stanje.ovire.length > 0}>
                  {posiljam ? 'Načrtujem …' : kdajIzvesti === 'zdaj' ? 'Strni zdaj' : 'Načrtuj strnitev'}
                </button>
              </div>
            </form>
          )}
        </>
      )}
    </section>
  );
}

export function StanjeBaze(): JSX.Element {
  const [stanje, setStanje] = useState<StanjeBazeTip | null>(null);
  const [napaka, setNapaka] = useState<string | null>(null);

  useEffect(() => {
    api
      .stanjeBaze()
      .then(setStanje)
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : 'Stanja baze ni bilo mogoče prebrati.');
      });
  }, []);

  if (napaka !== null) {
    return (
      <main>
        <h1>Stanje baze</h1>
        <p className="napaka">{napaka}</p>
      </main>
    );
  }
  if (stanje === null) {
    return (
      <main>
        <h1>Stanje baze</h1>
        <p>Berem …</p>
      </main>
    );
  }

  // Dnevnika ni mogoče vedno prebrati; takrat zasedenost pove samo podatke,
  // in to je tudi zapisano, da številka ne obljublja več, kot ve.
  const skupaj = stanje.baza + (stanje.dnevnik ?? 0);
  const zasedenost = stanje.disk === null ? null : Math.round((skupaj / stanje.disk) * 100);
  const knjizbSkupaj = stanje.knjizbe.reduce((n, k) => n + k.knjizb, 0);

  return (
    <main>
      <h1>Stanje baze</h1>
      <p className="podnaslov">
        Prostora ni mogoče zmanjšati, zato ga spremljamo, preden se napolni. Številke so takšne,
        kot jih pove baza sama.
      </p>

      <section className="kartica">
        <h2>Zasedenost</h2>
        <ul className="seznam">
          <li>
            Podatki <strong>{velikost(stanje.baza)}</strong>
          </li>
          <li>
            Dnevnik transakcij <strong>{velikostAli(stanje.dnevnik)}</strong>
            <span className="drobno">
              leži na istem disku; ob velikih uvozih zasede do približno 1 GB
              {stanje.dnevnik === null &&
                '. Velikosti ne sme brati vloga aplikacije, zato je spodnja zasedenost brez njega'}
            </span>
          </li>
          {stanje.disk !== null && (
            <li>
              Disk <strong>{velikost(stanje.disk)}</strong>
              <span className="drobno">
                zasedeno {velikost(skupaj)}
                {zasedenost === null ? '' : ` (${String(zasedenost)} %)`}
              </span>
            </li>
          )}
          {stanje.disk === null && (
            <li className="drobno">
              Velikost diska ni znana; nastavi okoljsko spremenljivko <code>DISK_GB</code>.
            </li>
          )}
        </ul>
      </section>

      <section className="kartica">
        <h2>Tabele</h2>
        <table className="knjizbe">
          <thead>
            <tr>
              <th>Tabela</th>
              <th className="stevilka">Podatki</th>
              <th className="stevilka">Indeksi</th>
              <th className="stevilka">Prazno</th>
              <th className="stevilka">Vrstic (ocena)</th>
              <th>Zadnje čiščenje</th>
            </tr>
          </thead>
          <tbody>
            {stanje.tabele.map((t) => (
              <tr key={t.ime}>
                <td>
                  <code>{t.ime}</code>
                </td>
                <td className="stevilka">{velikost(t.podatki)}</td>
                <td className="stevilka">{velikost(t.indeksi)}</td>
                <td className="stevilka">{delezProstega(t.prosto, t.podatki)}</td>
                <td className="stevilka">{t.vrstic === null ? '—' : stevilo(t.vrstic)}</td>
                <td>{kdaj(t.zadnjeCiscenje)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="namig">
          Izvirna vrstica pri knjižbi zaseda povprečno{' '}
          <strong>
            {stanje.izvirnaVrsticaPovprecno === null
              ? NI_PODATKA
              : `${stevilo(stanje.izvirnaVrsticaPovprecno)} B`}
          </strong>.
        </p>
        <p className="namig">
          Stolpec <em>Prazno</em> je prostor, ki ga tabela zaseda na disku, v njem pa ni več
          živih vrstic. To ni isto kot mrtve vrstice: te samodejno čiščenje sproti pobere,
          prostora pa ne vrne. Sprosti ga samo strnitev z izključno ključavnico (kartica
          spodaj).
        </p>
      </section>

      <KarticaStrnitve />

      <section className="kartica">
        <h2>Časi izrisov poročil</h2>
        {stanje.izrisi.meritev === 0 ? (
          <p className="namig">Od zadnjega zagona strežnika še ni bilo izrisa poročila.</p>
        ) : (
          <>
            <table className="knjizbe izrisi">
              <thead>
                <tr>
                  <th>Poročilo</th>
                  <th>Podjetje</th>
                  <th className="stevilka">Izrisov</th>
                  <th className="stevilka">Mediana</th>
                  <th className="stevilka">Najdaljši</th>
                  <th>Zadnji</th>
                </tr>
              </thead>
              <tbody>
                {stanje.izrisi.skupine.map((g) => (
                  <tr key={`${g.vrsta}-${g.podjetje}`}>
                    <td>{POROCILA[g.vrsta] ?? g.vrsta}</td>
                    <td>{g.naziv}</td>
                    <td className="stevilka">{stevilo(g.izrisov)}</td>
                    <td className="stevilka">{trajanje(g.mediana)}</td>
                    <td className="stevilka">{trajanje(g.najdaljsi)}</td>
                    <td>{datumInUra(g.zadnji)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <details className="zadnji-izrisi">
              <summary>Zadnjih {stevilo(stanje.izrisi.zadnji.length)} izrisov</summary>
              <table className="knjizbe">
                <tbody>
                  {stanje.izrisi.zadnji.map((z, i) => (
                    <tr key={`${z.kdaj}-${String(i)}`}>
                      <td>{datumInUra(z.kdaj)}</td>
                      <td>{POROCILA[z.vrsta] ?? z.vrsta}</td>
                      <td>{z.naziv}</td>
                      <td className="stevilka">{z.leto ?? ''}</td>
                      <td className="stevilka">{trajanje(z.ms)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          </>
        )}
        <p className="namig">
          Čas na strežniku od prihoda zahteve do konca odgovora, samo uspešni izrisi (izvozi ne).
          Hrani se zadnjih {stevilo(stanje.izrisi.najvec)} meritev v pomnilniku strežnika (zdaj{' '}
          {stevilo(stanje.izrisi.meritev)}); ob namestitvi se seznam izprazni. Kdo je poročilo odprl,
          se ne hrani.
        </p>
      </section>

      <section className="kartica">
        <h2>Pokrivalni indeks</h2>
        {(() => {
          const i = stanje.pokrivalniIndeks;
          const s = stanjeIndeksa(i);
          return (
            <>
              <p className={s.vRedu ? 'uspeh-besedilo' : 'opozorilo'}>
                <code>{i.ime}</code>: {s.besedilo}
              </p>
              {i.obstaja && (
                <ul className="seznam">
                  <li>
                    Velikost <strong>{velikostAli(i.velikost)}</strong>
                  </li>
                  <li>
                    Zgrajen <strong>{gradnjaIndeksa(i)}</strong>
                  </li>
                </ul>
              )}
            </>
          );
        })()}
        <p className="namig">
          Na tem indeksu stojijo vsa poročila. Gradi se v ozadju ob zagonu; brez njega poročila
          delujejo, a počasneje (docs/ARHITEKTURA.md, 5.7).
        </p>
      </section>

      <section className="kartica">
        <h2>Indeksi knjižb</h2>
        <div className="indeksi-ovoj">
          <table className="knjizbe">
            <thead>
              <tr>
                <th>Indeks</th>
                <th className="stevilka">Velikost</th>
                <th className="stevilka">Branj</th>
                <th>Zadnje branje</th>
                <th>Opomba</th>
              </tr>
            </thead>
            {poTabelah(stanje.indeksi).map(([tabela, indeksi]) => (
              <tbody key={tabela}>
                <tr className="skupina-indeksov">
                  <th colSpan={5}>
                    <code>{tabela}</code>
                  </th>
                </tr>
                {indeksi.map((i) => {
                  const opomba = opombaIndeksa(i);
                  return (
                    <tr key={i.ime} className={opomba.zaPregled ? 'za-pregled' : undefined}>
                      <td>
                        <code>{i.ime}</code>
                      </td>
                      <td className="stevilka">{velikost(i.velikost)}</td>
                      <td className="stevilka">{stevilo(i.branj)}</td>
                      <td>{i.zadnjeBranje === null ? '—' : datumInUra(i.zadnjeBranje)}</td>
                      <td>{opomba.besedilo}</td>
                    </tr>
                  );
                })}
              </tbody>
            ))}
          </table>
        </div>
        <p className="namig">
          Branja štejejo od {stanje.statistikaOd === null ? 'zadnje ponastavitve statistike' : datumInUra(stanje.statistikaOd)}.
          Indeks, ki varuje primarni ključ, enoličnost ali drugo omejitev (pravilo je v stolpcu
          Opomba), baza potrebuje tudi brez enega samega branja, zato ni kandidat za brisanje. Pri
          navadnem indeksu nič branj ne pomeni samodejno, da ni potreben: statistika mora zajeti
          dovolj dolgo obdobje (tudi uvoze in zaključke leta). Takšen indeks je le označen »za
          pregled«; brisanje je ločena odločitev.
        </p>
      </section>

      <section className="kartica">
        <h2>Arhiv izvirnih datotek</h2>
        <p className={stanje.arhiv.cezMejo ? 'opozorilo' : undefined}>
          Shranjenih izvirnikov <strong>{stevilo(stanje.arhiv.datotek)}</strong> — izvirno{' '}
          <strong>{velikost(stanje.arhiv.izvirno)}</strong>, stisnjeno{' '}
          <strong>{velikost(stanje.arhiv.shranjeno)}</strong>; opozorilni prag{' '}
          <strong>{velikost(stanje.arhiv.meja)}</strong>.
          {stanje.arhiv.cezMejo &&
            ' Arhiv je dosegel opozorilni prag: čas je za presojo selitve izvirnikov v zunanjo shrambo.'}
        </p>
        {stanje.arhiv.izbrisanih > 0 && (
          <p className="drobno">
            Po pravilih hrambe izbrisanih vsebin: {stevilo(stanje.arhiv.izbrisanih)} (zapis o
            njih ostane).
          </p>
        )}
        <p className="namig">
          Izvirnik vsakega uvoza je stisnjen shranjen v bazi. Izvirnik uvoza, ki velja, nima roka
          izbrisa; nadomeščeni, zavrnjeni in neuspeli se hranijo {stanje.arhiv.hrambaLet} leti od dne,
          ko je uvoz prenehal veljati. Opozorilni prag je 1 GB ali 20 % diska, kar je manj. Ni trda
          meja: datoteke v bazi podaljšujejo varnostno kopijo in njeno obnovo, zato se izvirniki
          preselijo v zunanjo shrambo, ko to postane moteče – lahko tudi prej
          (docs/ARHITEKTURA.md, 5.6).
        </p>
      </section>

      <KarticaIzvirnihVrstic m={stanje.izvirneVrstice} baza={stanje.baza} />

      <section className="kartica">
        <h2>Knjižbe po podjetjih in letih</h2>
        <p className="namig">Skupaj {stevilo(knjizbSkupaj)} knjižb.</p>
        <table className="knjizbe">
          <thead>
            <tr>
              <th>Podjetje</th>
              <th className="stevilka">Leto</th>
              <th className="stevilka">Knjižb</th>
            </tr>
          </thead>
          <tbody>
            {stanje.knjizbe.map((k) => (
              <tr key={`${k.podjetje}-${String(k.leto)}`}>
                <td>{k.naziv}</td>
                <td className="stevilka">{k.leto}</td>
                <td className="stevilka">{stevilo(k.knjizb)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </main>
  );
}
